import { Injectable, inject } from '@angular/core';
import {
  abortableRequest,
  AdditionalData,
  RosterContent,
  RosterEventRepository,
} from '@softapps/pms/roster';
import { ApiDataAccess, ApiResourceLocation } from '@softline/core';
import {
  SOFTLINE_API_GARAGE_ROSTER_CONTENT,
  SOFTLINE_API_GARAGE_ROSTER_CONTENT_DELETE,
  SOFTLINE_API_GARAGE_ROSTER_CONTENT_ENTRY,
  SOFTLINE_API_GARAGE_ROSTER_EMPLOYEE_CONTENT,
  SOFTLINE_API_GARAGE_ROSTER_EMPLOYEE_CONTENT_DETAIL,
} from '../roster.api';

@Injectable()
export class GarageRosterEventRepository extends RosterEventRepository<
  RosterContent,
  AdditionalData
> {
  readonly #dataAccess = inject(ApiDataAccess);

  async loadEvents(
    employeeId: number,
    begintime: string,
    endtime: string,
    employeeIdPerson: number,
    abortSignal?: AbortSignal
  ): Promise<RosterContent[]> {
    const location: ApiResourceLocation = {
      path: SOFTLINE_API_GARAGE_ROSTER_EMPLOYEE_CONTENT,
      pathParams: { employeeId },
      queryParams: {
        begindate: begintime,
        enddate: endtime,
        employeeIdPerson: employeeIdPerson,
      },
    };

    return abortableRequest(
      this.#dataAccess.get$<RosterContent[]>(location),
      abortSignal
    );
  }

  async loadEventDetail(
    employeeId: number,
    eventId: string | number
  ): Promise<AdditionalData> {
    const location: ApiResourceLocation = {
      path: SOFTLINE_API_GARAGE_ROSTER_EMPLOYEE_CONTENT_DETAIL,
      pathParams: { employeeId, id: eventId },
    };

    return this.#dataAccess.get<AdditionalData>(location);
  }

  async createEvent(payload: Partial<RosterContent>): Promise<RosterContent> {
    const location: ApiResourceLocation = {
      path: SOFTLINE_API_GARAGE_ROSTER_CONTENT,
    };

    const result: RosterContent[] = await this.#dataAccess.create(location, payload);

    return result[0];
  }

  async updateEvent(event: RosterContent): Promise<RosterContent> {
    const location: ApiResourceLocation = {
      path: SOFTLINE_API_GARAGE_ROSTER_CONTENT_ENTRY,
      pathParams: { id: event.id },
    };

    return await this.#dataAccess.update(location, event);
  }

  async deleteEvent(event: RosterContent): Promise<RosterContent> {
    const result = await this.deleteEvents([event]);
    return result[0];
  }

  async deleteEvents(events: RosterContent[]): Promise<RosterContent[]> {
    const location: ApiResourceLocation = {
      path: SOFTLINE_API_GARAGE_ROSTER_CONTENT_DELETE,
    };

    const result: RosterContent[] = await this.#dataAccess.create(location, events);

    return result;
  }

  async delete(id: number): Promise<void> {
    const location: ApiResourceLocation = {
      path: SOFTLINE_API_GARAGE_ROSTER_CONTENT_DELETE,
      pathParams: { id: id.toString() },
    };
    await this.#dataAccess.delete(location);
  }
}
